/**
 * DisclaimerSettings — admin tab for the App Settings page.
 *
 * Three sections:
 * 1. Editor — title, content (markdown with live preview), is_enabled toggle, Save.
 * 2. Reset acceptances — confirmation modal, then POST /api/admin/disclaimer/reset.
 * 3. Acceptance log — paginated table of who accepted, when, with audit metadata.
 */

import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { RotateCcw } from 'lucide-react'

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Textarea,
  useToast,
  Spinner,
} from '@/components/ui'
import {
  useAcceptanceLog,
  useAdminDisclaimer,
  useResetAcceptances,
  useUpdateDisclaimer,
} from '@/hooks/useDisclaimer'

const DEFAULT_TITLE = 'Terms and Conditions'
const DEFAULT_CONTENT = '# Welcome\n\nPlease review these terms and click **Accept** to continue.'

export function DisclaimerSettings() {
  const { addToast } = useToast()
  const { data: current, isLoading: loadingCurrent } = useAdminDisclaimer()
  const updateMutation = useUpdateDisclaimer()
  const resetMutation = useResetAcceptances()
  const { data: log, isLoading: loadingLog, refetch: refetchLog } = useAcceptanceLog()

  const [title, setTitle] = useState(DEFAULT_TITLE)
  const [content, setContent] = useState(DEFAULT_CONTENT)
  const [enabled, setEnabled] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)

  // When the current disclaimer loads, seed the form fields from it.
  useEffect(() => {
    if (current) {
      setTitle(current.title)
      setContent(current.content)
      setEnabled(current.is_enabled)
    }
  }, [current])

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({ title, content, is_enabled: enabled })
      addToast({ type: 'success', title: 'Saved' })
    } catch (e) {
      addToast({ type: 'error', title: 'Save failed', description: (e as Error).message })
    }
  }

  const handleReset = async () => {
    try {
      const res = await resetMutation.mutateAsync()
      setShowResetConfirm(false)
      addToast({
        type: 'success',
        title: 'Acceptances reset',
        description: `${res.deleted_count} user${res.deleted_count === 1 ? '' : 's'} will be re-prompted on next login.`,
      })
      refetchLog()
    } catch (e) {
      addToast({ type: 'error', title: 'Reset failed', description: (e as Error).message })
    }
  }

  return (
    <div className="space-y-6">
      {/* ── Editor ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Disclaimer / Terms of Service</CardTitle>
          <CardDescription>
            Configure the modal shown to users on first login. Changes save a new version;
            existing acceptances stay valid until you click Reset.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingCurrent ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <input
                  id="enabled"
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                <label htmlFor="enabled" className="text-sm font-medium">
                  Show disclaimer on first login
                </label>
              </div>

              <Input
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Terms and Conditions"
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">
                    Content (Markdown)
                  </label>
                  <Textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={16}
                    className="font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Preview</label>
                  <div className="prose prose-sm dark:prose-invert max-w-none border border-border rounded-md p-3 min-h-64 overflow-y-auto">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || '_(empty)_'}</ReactMarkdown>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSave} isLoading={updateMutation.isPending}>
                  Save
                </Button>
              </div>

              {current && (
                <p className="text-xs text-muted-foreground">
                  Current version: {current.version}
                  {current.updated_at && ` · last updated ${new Date(current.updated_at).toLocaleString()}`}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Reset acceptances ──────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Force re-acceptance</CardTitle>
          <CardDescription>
            Reset every user's acceptance for the current disclaimer. They'll see the modal again on
            next login (or within ~15 minutes mid-session).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="ghost"
            onClick={() => setShowResetConfirm(true)}
            disabled={!current}
            leftIcon={<RotateCcw className="h-4 w-4" />}
          >
            Reset all acceptances
          </Button>
        </CardContent>
      </Card>

      {/* ── Acceptance log ────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Acceptance log</CardTitle>
          <CardDescription>
            Audit record of who has accepted the current disclaimer.
            {log && ` Total: ${log.total}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingLog ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Spinner />
            </div>
          ) : !log || log.items.length === 0 ? (
            <Alert variant="info">
              <AlertDescription>No acceptances yet.</AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground border-b border-border">
                  <tr>
                    <th className="py-2 pr-4">User</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Version</th>
                    <th className="py-2 pr-4">Accepted at</th>
                    <th className="py-2 pr-4">IP</th>
                    <th className="py-2">User agent</th>
                  </tr>
                </thead>
                <tbody>
                  {log.items.map((entry) => (
                    <tr key={`${entry.user_id}-${entry.disclaimer_id}`} className="border-b border-border">
                      <td className="py-2 pr-4">{entry.user_display_name}</td>
                      <td className="py-2 pr-4">{entry.user_email}</td>
                      <td className="py-2 pr-4">{entry.version}</td>
                      <td className="py-2 pr-4">{new Date(entry.accepted_at).toLocaleString()}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{entry.ip_address || '—'}</td>
                      <td className="py-2 truncate max-w-md text-xs text-muted-foreground">
                        {entry.user_agent || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Reset confirmation modal ──────────────────────────── */}
      <Modal open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Reset all acceptances?</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-foreground">
              Every user will be required to accept the current disclaimer again. This action
              cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setShowResetConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleReset} isLoading={resetMutation.isPending}>
              Reset acceptances
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
